import type { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { DEFAULT_SCOPE_NAME } from '@recruitee/ngrx';

import type { SlackIntegrationHttp } from '../types';
import { SlackIntegrationHttpService } from './slack-integration-http.service';
import { SlackIntegrationActions } from './slack-integration-store';

@Injectable({ providedIn: 'root' })
export class SlackIntegrationEffects {
  public fetchIntegration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SlackIntegrationActions.fetchCollection),
      mergeMap(() =>
        this.slackIntegrationHttpService.getCollection().pipe(
          map(data =>
            SlackIntegrationActions.fetchCollectionSuccess({
              payload: { data: data, scope: DEFAULT_SCOPE_NAME },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              SlackIntegrationActions.fetchCollectionFailed({
                payload: { error, scope: DEFAULT_SCOPE_NAME },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public deleteIntegration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SlackIntegrationActions.removeOnePessimistic),
      mergeMap(({ payload }) =>
        this.slackIntegrationHttpService.deleteIntegration(payload.data).pipe(
          map(data => SlackIntegrationActions.removeOnePessimisticSuccess({ payload: { data } })),
          map(response => SlackIntegrationActions.fetchCollection()),
          catchError((error: HttpErrorResponse) =>
            of(
              SlackIntegrationActions.removeOnePessimisticFailed({
                payload: { error, data: payload.data },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public disconnectAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SlackIntegrationActions.disconnectIntegrationCollection),
      mergeMap(() =>
        this.slackIntegrationHttpService
          .disconnectAll()
          .pipe(map(() => SlackIntegrationActions.disconnectIntegrationsCollectionSuccess())),
      ),
    ),
  );

  public updateIntegration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SlackIntegrationActions.updateOnePessimistic),
      mergeMap(({ payload }) =>
        this.slackIntegrationHttpService.updateIntegration(<SlackIntegrationHttp>payload.data).pipe(
          map(response => SlackIntegrationActions.updateOnePessimisticSuccess(response)),
          map(response => SlackIntegrationActions.fetchCollection()),
          catchError((error: HttpErrorResponse) =>
            of(
              SlackIntegrationActions.updateOnePessimisticFailed({
                payload: { error, data: payload.data },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private slackIntegrationHttpService: SlackIntegrationHttpService,
  ) {}
}
